import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import CampaignIcon from "@mui/icons-material/Campaign";
import DragIndicator from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAccount } from "../../../../context";
import { useToast } from "../../../../context/ToastContext";
import {
  DEFAULT_FIELD_LABELS,
  DEFAULT_VISIBLE_FIELD_IDS,
  LEAD_QUALITY_FIELD_OPTIONS,
  LEAD_VISIBLE_FIELD_OPTIONS,
} from "../constants/lead-visible-fields";
import { LeadContactsService } from "../services/leadContactsService";
import {
  CampaignExtraFieldConfig,
  CampaignGroup,
  CampaignVisibleFieldsConfig,
} from "../types/lead-contacts.types";

const PRIMARY = "#00a884";
const PRIMARY_DARK = "#008f6f";

interface CampaignVisibleFieldsConfigModalProps {
  open: boolean;
  onClose: () => void;
  groupedContacts: Record<string, CampaignGroup>;
  campaignVisibleFieldsConfig: Record<number, CampaignVisibleFieldsConfig>;
  /** Chamado após salvar; pode ser async. Recarregar config e leads aqui. */
  onSaved: () => void | Promise<void>;
}

const CampaignVisibleFieldsConfigModal: React.FC<
  CampaignVisibleFieldsConfigModalProps
> = ({
  open,
  onClose,
  groupedContacts,
  campaignVisibleFieldsConfig,
  onSaved,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { account } = useAccount();
  const { showError, showToast } = useToast();
  const [editingCampaign, setEditingCampaign] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [fieldLabels, setFieldLabels] = useState<Record<string, string>>({});
  const [extraFields, setExtraFields] = useState<CampaignExtraFieldConfig[]>(
    [],
  );
  /** Campos qualidade (só para campanhas Controle de Qualidade). */
  const [qualityFields, setQualityFields] = useState<
    CampaignExtraFieldConfig[]
  >([]);
  /** Ordem de exibição: ids dos campos padrão, chaves dos extras e dos quality. */
  const [orderedFieldIds, setOrderedFieldIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [editTab, setEditTab] = useState(0);
  /** Campos extras da campanha vindos do CRM (campaigns_campaign_config). */
  const [crmExtraFields, setCrmExtraFields] = useState<
    Array<{ field_name: string; field_type: string }>
  >([]);
  const [loadingCrmExtraFields, setLoadingCrmExtraFields] = useState(false);

  const isQualityCampaign = editingCampaign
    ? (groupedContacts[editingCampaign.name]?.is_quality ?? false)
    : false;

  const campaignList = Object.entries(groupedContacts)
    .map(([name, group]) => ({
      name,
      campaign_id: group.campaign_id ?? group.contacts?.[0]?.campaign_id,
    }))
    .filter(
      (c): c is { name: string; campaign_id: number } =>
        c.campaign_id != null && !isNaN(c.campaign_id),
    );

  const startEdit = useCallback(
    (campaignId: number, campaignName: string) => {
      const config = campaignVisibleFieldsConfig[campaignId];
      const isQuality = groupedContacts[campaignName]?.is_quality ?? false;
      const visible =
        config?.visible_fields != null && config.visible_fields.length > 0
          ? [...config.visible_fields]
          : [...DEFAULT_VISIBLE_FIELD_IDS];
      const extras = config?.extra_fields ?? [];
      const quality =
        config?.quality_fields != null && config.quality_fields.length > 0
          ? [...config.quality_fields]
          : isQuality
            ? LEAD_QUALITY_FIELD_OPTIONS.map((o) => ({
                key: o.id,
                label: o.label,
              }))
            : [];
      setSelectedFields(visible);
      setFieldLabels({ ...DEFAULT_FIELD_LABELS, ...config?.field_labels });
      setExtraFields(extras);
      setQualityFields(quality);
      const extraKeys = extras.map((e) => e.key);
      const qualityKeys = quality.map((e) => e.key);
      const visibleSet = new Set([...visible, ...extraKeys, ...qualityKeys]);
      const order =
        config?.field_order?.length &&
        config.field_order.some((id) => visibleSet.has(id))
          ? config.field_order.filter((id) => visibleSet.has(id))
          : [...visible, ...extraKeys, ...qualityKeys];
      setOrderedFieldIds(order);
      setEditingCampaign({ id: campaignId, name: campaignName });
      setEditTab(0);
    },
    [campaignVisibleFieldsConfig, groupedContacts],
  );

  /** Carrega campos extras do CRM ao editar uma campanha. */
  useEffect(() => {
    if (!editingCampaign || !account?.company_id) {
      setCrmExtraFields([]);
      return;
    }
    let cancelled = false;
    setLoadingCrmExtraFields(true);
    LeadContactsService.getCampaignExtraFields(
      Number(account.company_id),
      editingCampaign.id,
    )
      .then((list) => {
        if (!cancelled) setCrmExtraFields(list ?? []);
      })
      .catch(() => {
        if (!cancelled) {
          setCrmExtraFields([]);
          showError("Erro ao carregar campos extras do CRM.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCrmExtraFields(false);
      });
    return () => {
      cancelled = true;
    };
  }, [editingCampaign?.id, account?.company_id, showError]);

  /** Chaves de campos extras para a aba: CRM + keys já salvas na config (evita perder campos removidos do CRM). */
  const discoveredExtraKeys = useMemo(() => {
    const fromCrm = crmExtraFields.map((f) => f.field_name);
    const config = editingCampaign
      ? campaignVisibleFieldsConfig[editingCampaign.id]
      : undefined;
    const fromConfig = (config?.extra_fields ?? [])
      .map((e) => e.key)
      .filter((k) => !fromCrm.includes(k));
    return [...new Set([...fromCrm, ...fromConfig])].sort();
  }, [crmExtraFields, editingCampaign, campaignVisibleFieldsConfig]);

  const handleToggleStandardField = useCallback((fieldId: string) => {
    setSelectedFields((prev) => {
      const next = prev.includes(fieldId)
        ? prev.filter((id) => id !== fieldId)
        : [...prev, fieldId];
      return next;
    });
    setOrderedFieldIds((prev) =>
      prev.includes(fieldId)
        ? prev.filter((id) => id !== fieldId)
        : [...prev, fieldId],
    );
  }, []);

  const handleLabelChange = useCallback((fieldId: string, label: string) => {
    setFieldLabels((prev) => ({ ...prev, [fieldId]: label }));
  }, []);

  const handleExtraToggle = useCallback((key: string) => {
    setExtraFields((prev) => {
      const exists = prev.some((e) => e.key === key);
      if (exists) return prev.filter((e) => e.key !== key);
      return [...prev, { key, label: key }];
    });
    setOrderedFieldIds((prev) =>
      prev.includes(key) ? prev.filter((id) => id !== key) : [...prev, key],
    );
  }, []);

  const handleExtraLabelChange = useCallback((key: string, label: string) => {
    setExtraFields((prev) =>
      prev.map((e) => (e.key === key ? { ...e, label } : e)),
    );
  }, []);

  const handleQualityToggle = useCallback((key: string) => {
    setQualityFields((prev) => {
      const exists = prev.some((e) => e.key === key);
      if (exists) return prev.filter((e) => e.key !== key);
      return [
        ...prev,
        {
          key,
          label:
            LEAD_QUALITY_FIELD_OPTIONS.find((o) => o.id === key)?.label ?? key,
        },
      ];
    });
    setOrderedFieldIds((prev) =>
      prev.includes(key) ? prev.filter((id) => id !== key) : [...prev, key],
    );
  }, []);

  const handleQualityLabelChange = useCallback((key: string, label: string) => {
    setQualityFields((prev) =>
      prev.map((e) => (e.key === key ? { ...e, label } : e)),
    );
  }, []);

  const handleSelectAllStandard = useCallback(() => {
    const allIds = LEAD_VISIBLE_FIELD_OPTIONS.map((f) => f.id);
    setSelectedFields(allIds);
    setOrderedFieldIds((prev) => {
      const set = new Set(prev);
      const toAdd = allIds.filter((id) => !set.has(id));
      return toAdd.length ? [...prev, ...toAdd] : prev;
    });
  }, []);

  const handleDeselectAllStandard = useCallback(() => {
    setSelectedFields([]);
    setOrderedFieldIds((prev) =>
      prev.filter((id) => !LEAD_VISIBLE_FIELD_OPTIONS.some((o) => o.id === id)),
    );
  }, []);

  const handleSelectAllQuality = useCallback(() => {
    const all = LEAD_QUALITY_FIELD_OPTIONS.map((o) => ({
      key: o.id,
      label: o.label,
    }));
    setQualityFields(all);
    setOrderedFieldIds((prev) => {
      const set = new Set(prev);
      const toAdd = all.map((e) => e.key).filter((id) => !set.has(id));
      return toAdd.length ? [...prev, ...toAdd] : prev;
    });
  }, []);

  const handleDeselectAllQuality = useCallback(() => {
    setQualityFields([]);
    setOrderedFieldIds((prev) =>
      prev.filter((id) => !LEAD_QUALITY_FIELD_OPTIONS.some((o) => o.id === id)),
    );
  }, []);

  const getOrderLabel = useCallback(
    (id: string) => {
      const standard = LEAD_VISIBLE_FIELD_OPTIONS.find((o) => o.id === id);
      if (standard) return (fieldLabels[id] ?? standard.label).trim() || id;
      const extra = extraFields.find((e) => e.key === id);
      if (extra) return (extra.label ?? id).trim() || id;
      const quality = qualityFields.find((e) => e.key === id);
      if (quality) return (quality.label ?? id).trim() || id;
      const qualityOpt = LEAD_QUALITY_FIELD_OPTIONS.find((o) => o.id === id);
      return (qualityOpt?.label ?? id).trim() || id;
    },
    [fieldLabels, extraFields, qualityFields],
  );

  const moveOrder = useCallback((index: number, direction: "up" | "down") => {
    setOrderedFieldIds((prev) => {
      const next = [...prev];
      const target = direction === "up" ? index - 1 : index + 1;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }, []);

  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDraggingIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
    e.dataTransfer.setData("application/x-order-index", String(index));
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, toIndex: number) => {
    e.preventDefault();
    setDragOverIndex(null);
    setDraggingIndex(null);
    const fromIndex = parseInt(
      e.dataTransfer.getData("application/x-order-index") ??
        e.dataTransfer.getData("text/plain") ??
        "-1",
      10,
    );
    if (fromIndex < 0 || fromIndex === toIndex) return;
    setOrderedFieldIds((prev) => {
      const next = [...prev];
      const [removed] = next.splice(fromIndex, 1);
      const insertAt = toIndex > fromIndex ? toIndex - 1 : toIndex;
      next.splice(insertAt, 0, removed);
      return next;
    });
  }, []);

  const handleDragEnd = useCallback(() => {
    setDragOverIndex(null);
    setDraggingIndex(null);
  }, []);

  const handleSave = useCallback(async () => {
    if (!editingCampaign || !account?.company_id) return;
    setSaving(true);
    try {
      await LeadContactsService.setCampaignVisibleFields(
        Number(account.company_id),
        editingCampaign.id,
        {
          visible_fields: selectedFields,
          field_labels: fieldLabels,
          extra_fields: extraFields,
          quality_fields: isQualityCampaign ? qualityFields : undefined,
          field_order: orderedFieldIds.length ? orderedFieldIds : undefined,
          campaign_name: editingCampaign.name,
        },
      );
      await onSaved();
      showToast({
        type: "success",
        message: "Configuração salva para esta campanha.",
      });
      setEditingCampaign(null);
    } catch (e) {
      showError("Erro ao salvar configuração.");
    } finally {
      setSaving(false);
    }
  }, [
    editingCampaign,
    account?.company_id,
    selectedFields,
    fieldLabels,
    extraFields,
    qualityFields,
    orderedFieldIds,
    isQualityCampaign,
    onSaved,
    showToast,
    showError,
  ]);

  const handleCloseEdit = useCallback(() => {
    setEditingCampaign(null);
  }, []);

  useEffect(() => {
    if (!open) setEditingCampaign(null);
  }, [open]);

  return (
    <>
      {/* Lista de campanhas */}
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        slotProps={{
          paper: {
            sx: {
              borderRadius: isMobile ? 0 : 2,
              boxShadow: isMobile ? "none" : "0 8px 32px rgba(0,0,0,0.12)",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            py: 2,
            px: { xs: 2, sm: 3 },
            borderBottom: "1px solid",
            borderColor: "divider",
            background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
            color: "white",
          }}
        >
          <SettingsIcon sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              Campos visíveis por campanha
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.25 }}>
              Defina o que os coordenadores podem ver e copiar
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
            Selecione uma campanha para configurar quais campos ficam
            disponíveis na cópia e na visualização dos leads.
          </Typography>
          {campaignList.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
                px: 2,
                borderRadius: 2,
                bgcolor: "action.hover",
              }}
            >
              <CampaignIcon
                sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                Nenhuma campanha carregada. Expanda as campanhas na lista para
                que os IDs fiquem disponíveis.
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {campaignList.map(({ name, campaign_id }) => (
                <ListItem
                  key={campaign_id}
                  disablePadding
                  sx={{
                    mb: 1,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    overflow: "hidden",
                  }}
                >
                  <ListItemButton
                    onClick={() => startEdit(campaign_id, name)}
                    sx={{
                      py: 1,
                      px: 2,
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <CampaignIcon sx={{ color: PRIMARY, fontSize: 22 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={name}
                      slotProps={{
                        primary: {
                          fontWeight: 600,
                          fontSize: "0.95rem",
                        },
                        secondary: {
                          variant: "caption",
                          color: "text.secondary",
                        },
                      }}
                      secondary={`ID: ${campaign_id}`}
                    />
                    <EditIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edição de campos da campanha */}
      <Dialog
        open={editingCampaign != null}
        onClose={handleCloseEdit}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        slotProps={{
          paper: {
            sx: {
              borderRadius: isMobile ? 0 : 2,
              boxShadow: isMobile ? "none" : "0 8px 32px rgba(0,0,0,0.12)",
              maxHeight: isMobile ? "100%" : "90vh",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            py: 1.5,
            px: { xs: 2, sm: 3 },
            borderBottom: "1px solid",
            borderColor: "divider",
            background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
            color: "white",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {editingCampaign?.name}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Configurar campos visíveis
            </Typography>
          </Box>
        </DialogTitle>

        <Tabs
          value={editTab}
          onChange={(_, v) => setEditTab(v)}
          variant="fullWidth"
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            px: { xs: 1, sm: 2 },
            minHeight: 48,
            "& .MuiTab-root": {
              minHeight: 48,
              textTransform: "none",
              fontWeight: 600,
            },
            "& .Mui-selected": { color: PRIMARY },
            "& .MuiTabs-indicator": { backgroundColor: PRIMARY },
          }}
        >
          <Tab label="Campos padrão" />
          <Tab label="Campos extras" />
          {isQualityCampaign && <Tab label="Campos qualidade" />}
          <Tab label="Ordem" />
        </Tabs>

        <DialogContent
          sx={{ p: 0, display: "flex", flexDirection: "column", minHeight: 0 }}
        >
          {editTab === 0 && (
            <Box sx={{ p: { xs: 2, sm: 3 }, overflow: "auto", flex: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Marque os campos que podem ser vistos e copiados. Opcionalmente
                altere o rótulo exibido.
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSelectAllStandard}
                  sx={{ textTransform: "none" }}
                >
                  Marcar todos
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleDeselectAllStandard}
                  sx={{ textTransform: "none" }}
                >
                  Desmarcar todos
                </Button>
              </Box>
              <List dense disablePadding>
                {LEAD_VISIBLE_FIELD_OPTIONS.map((opt) => (
                  <ListItem key={opt.id} disablePadding sx={{ py: 0.75 }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "220px 1fr" },
                        gap: 2,
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedFields.includes(opt.id)}
                            onChange={() => handleToggleStandardField(opt.id)}
                            sx={{
                              color: PRIMARY,
                              "&.Mui-checked": { color: PRIMARY },
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2" fontWeight={500}>
                            {opt.label}
                          </Typography>
                        }
                      />
                      <TextField
                        size="small"
                        label="Rótulo na cópia"
                        value={fieldLabels[opt.id] ?? opt.label}
                        onChange={(e) =>
                          handleLabelChange(opt.id, e.target.value)
                        }
                        fullWidth
                        placeholder={opt.label}
                      />
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {isQualityCampaign && editTab === 2 && (
            <Box sx={{ p: { xs: 2, sm: 3 }, overflow: "auto", flex: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Campos exclusivos de Controle de Qualidade: marque e defina o
                rótulo para cópia e visualização.
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSelectAllQuality}
                  sx={{ textTransform: "none" }}
                >
                  Marcar todos
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleDeselectAllQuality}
                  sx={{ textTransform: "none" }}
                >
                  Desmarcar todos
                </Button>
              </Box>
              <List dense disablePadding>
                {LEAD_QUALITY_FIELD_OPTIONS.map((opt) => {
                  const config = qualityFields.find((e) => e.key === opt.id);
                  const isSelected = !!config;
                  return (
                    <ListItem key={opt.id} disablePadding sx={{ py: 0.75 }}>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "220px 1fr" },
                          gap: 2,
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleQualityToggle(opt.id)}
                              sx={{
                                color: PRIMARY,
                                "&.Mui-checked": { color: PRIMARY },
                              }}
                            />
                          }
                          label={
                            <Typography variant="body2" fontWeight={500}>
                              {opt.label}
                            </Typography>
                          }
                        />
                        <TextField
                          size="small"
                          label="Rótulo na cópia"
                          value={config?.label ?? opt.label}
                          onChange={(e) =>
                            handleQualityLabelChange(opt.id, e.target.value)
                          }
                          disabled={!isSelected}
                          fullWidth
                          placeholder={opt.label}
                        />
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}

          {editTab === (isQualityCampaign ? 3 : 2) && (
            <Box sx={{ p: { xs: 2, sm: 3 }, overflow: "auto", flex: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Defina a ordem em que os campos aparecem na cópia e na
                visualização do lead. Arraste os itens ou use as setas para
                subir ou descer.
              </Typography>
              {orderedFieldIds.length === 0 ? (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 4,
                    bgcolor: "action.hover",
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Nenhum campo selecionado. Configure os campos nas abas
                    &quot;Campos padrão&quot;, &quot;Campos extras&quot;
                    {isQualityCampaign ? " e &quot;Campos qualidade&quot;" : ""}
                    .
                  </Typography>
                </Box>
              ) : (
                <List dense disablePadding>
                  {orderedFieldIds.map((id, index) => (
                    <Box
                      key={id}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, index)}
                      sx={{
                        position: "relative",
                        "&::before":
                          dragOverIndex === index
                            ? {
                                content: '""',
                                position: "absolute",
                                left: 0,
                                right: 0,
                                top: -2,
                                height: 3,
                                borderRadius: 2,
                                bgcolor: PRIMARY,
                                zIndex: 1,
                              }
                            : undefined,
                      }}
                    >
                      <ListItem
                        disablePadding
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragEnd={handleDragEnd}
                        sx={{
                          py: 0.75,
                          px: 1,
                          borderRadius: 1,
                          cursor: "grab",
                          opacity: draggingIndex === index ? 0.6 : 1,
                          bgcolor:
                            draggingIndex === index
                              ? "action.selected"
                              : undefined,
                          "&:hover": { bgcolor: "action.hover" },
                          "&:active": { cursor: "grabbing" },
                        }}
                        secondaryAction={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => moveOrder(index, "up")}
                              disabled={index === 0}
                              sx={{ minWidth: 36, p: 0.5 }}
                              aria-label="Subir"
                            >
                              <ArrowUpward sx={{ fontSize: 18 }} />
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => moveOrder(index, "down")}
                              disabled={index === orderedFieldIds.length - 1}
                              sx={{ minWidth: 36, p: 0.5 }}
                              aria-label="Descer"
                            >
                              <ArrowDownward sx={{ fontSize: 18 }} />
                            </Button>
                          </Box>
                        }
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Tooltip title="Arrastar para reordenar" arrow>
                            <DragIndicator
                              sx={{ color: "text.secondary", fontSize: 20 }}
                            />
                          </Tooltip>
                        </ListItemIcon>
                        <ListItemText
                          primary={getOrderLabel(id)}
                          secondary={
                            LEAD_VISIBLE_FIELD_OPTIONS.some((o) => o.id === id)
                              ? undefined
                              : id
                          }
                          primaryTypographyProps={{
                            variant: "body2",
                            fontWeight: 500,
                          }}
                          secondaryTypographyProps={{
                            variant: "caption",
                            fontFamily: "monospace",
                          }}
                        />
                      </ListItem>
                    </Box>
                  ))}
                </List>
              )}
            </Box>
          )}

          {editTab === 1 && (
            <Box sx={{ p: { xs: 2, sm: 3 }, overflow: "auto", flex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 0.5,
                  mb: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Campos extras (CRM): marque e defina o título para exibição.
                </Typography>
                <Tooltip
                  title="Campos definidos na tabela campaigns_campaign_config do CRM (PostgreSQL)."
                  arrow
                  placement="top"
                >
                  <InfoOutlinedIcon
                    sx={{
                      fontSize: 18,
                      color: "info.main",
                      flexShrink: 0,
                      mt: 0.25,
                    }}
                  />
                </Tooltip>
              </Box>
              {loadingCrmExtraFields ? (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 4,
                    bgcolor: "action.hover",
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Carregando campos extras do CRM...
                  </Typography>
                </Box>
              ) : discoveredExtraKeys.length === 0 ? (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 4,
                    bgcolor: "action.hover",
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Nenhum campo extra configurado no CRM para esta campanha
                    (campaigns_campaign_config).
                  </Typography>
                </Box>
              ) : (
                <List dense disablePadding>
                  {discoveredExtraKeys.map((key) => {
                    const config = extraFields.find((e) => e.key === key);
                    const isSelected = !!config;
                    return (
                      <ListItem key={key} disablePadding sx={{ py: 0.75 }}>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "220px 1fr" },
                            gap: 2,
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              minWidth: 0,
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() => handleExtraToggle(key)}
                                  sx={{
                                    color: PRIMARY,
                                    "&.Mui-checked": { color: PRIMARY },
                                  }}
                                />
                              }
                              label=""
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.8rem",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              title={key}
                            >
                              {key}
                            </Typography>
                          </Box>
                          <TextField
                            size="small"
                            label="Rótulo"
                            value={config?.label ?? ""}
                            onChange={(e) =>
                              handleExtraLabelChange(key, e.target.value)
                            }
                            disabled={!isSelected}
                            fullWidth
                            placeholder={key}
                          />
                        </Box>
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Button onClick={handleCloseEdit} sx={{ textTransform: "none" }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              textTransform: "none",
              bgcolor: PRIMARY,
              "&:hover": { bgcolor: PRIMARY_DARK },
            }}
          >
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CampaignVisibleFieldsConfigModal;
